import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  AccountEventView,
  AccountView,
  AssigneeDirectory,
  AssignRoleInput,
  CreateAccountInput,
  DeactivateAccountInput,
  InvitationOutcome,
  RenameAccountInput,
  ResetPasswordInput,
  SetAdministrationInput,
} from "../../shared/contracts.js";
import { isClientApiError, request } from "./http.js";

/**
 * Account administration queries and mutations (spec §2 "Account
 * administration"), against the Task 3 routes:
 *
 * - `GET   /api/accounts`                    → `{ items: AccountView[] }`
 * - `GET   /api/accounts/history`            → `{ items: AccountEventView[] }`
 * - `POST  /api/accounts`                    → `{ user, temporaryPassword, invitation }`
 * - `PATCH /api/accounts/:id/name`           → `AccountView` (the same name again: unchanged row)
 * - `PATCH /api/accounts/:id/role`           → `AccountView`
 * - `POST  /api/accounts/:id/deactivate`     → `AccountView`
 * - `POST  /api/accounts/:id/reset-password` → `{ temporaryPassword, invitation }`
 * - `POST  /api/accounts/:id/administration` → `AccountView` (grant / revoke)
 *
 * The temporary password is the one secret this module ever touches, and it
 * is handled in exactly one way: the request function hands it to the
 * caller's `onTemporaryPassword` callback and then drops it, so the value the
 * mutation *resolves* with — the value React Query stores in its mutation
 * cache — never contains it. The component keeps it in state for as long as
 * its dialog is open and clears it on close. It is never written to a query,
 * the URL, storage or a log. The same holds on the way *in*: a temporary
 * password the administrator typed (owner decision 2026-09-17) is read at
 * send time through the hook's `chosenTemporaryPassword` accessor, never
 * passed as a mutation variable — variables are cached too — so it exists
 * only in the dialog's own state until that dialog closes.
 *
 * The history query's key extends the list's, so the one invalidation every
 * mutation already issues — `{ queryKey: ["accounts"] }`, a prefix match —
 * refreshes "Recent changes" along with the table.
 */

export type { AccountEventView, AccountView };

export const accountsQueryKey = ["accounts"] as const;
export const accountHistoryQueryKey = ["accounts", "history"] as const;

export const accountPaths = {
  list: "/api/accounts",
  history: "/api/accounts/history",
  create: "/api/accounts",
  name: (userId: string) => `/api/accounts/${encodeURIComponent(userId)}/name`,
  role: (userId: string) => `/api/accounts/${encodeURIComponent(userId)}/role`,
  deactivate: (userId: string) =>
    `/api/accounts/${encodeURIComponent(userId)}/deactivate`,
  resetPassword: (userId: string) =>
    `/api/accounts/${encodeURIComponent(userId)}/reset-password`,
  administration: (userId: string) =>
    `/api/accounts/${encodeURIComponent(userId)}/administration`,
} as const;

// ---------------------------------------------------------------------------
// Error copy
// ---------------------------------------------------------------------------

/** The one recovery message for an optimistic-version conflict (409 `STALE_WRITE`). */
export const ACCOUNT_STALE_MESSAGE =
  "This account changed after you opened it. The list has been reloaded — check it and try again.";

/** Plain-language copy for a failed account mutation. */
export function accountMutationErrorMessage(error: unknown): string {
  if (isClientApiError(error)) {
    if (error.status === 409 && error.code === "STALE_WRITE")
      return ACCOUNT_STALE_MESSAGE;
    if (error.status === 0) {
      return "We can't reach the server right now. Nothing was changed. Check your connection and try again.";
    }
    if (error.status === 403) {
      return "Your account can't manage accounts. Only an administrator can.";
    }
    if (error.status === 409 && error.code === "LAST_ADMINISTRATOR") {
      return "That account is the last active administrator, so it can't lose administration or be deactivated. Make someone else an administrator first.";
    }
    if (error.status === 422) {
      const first = Object.values(error.fieldErrors)
        .flat()
        .find((message) => message.length > 0);
      return first ?? error.message;
    }
    return error.message;
  }
  return "Something went wrong. Nothing was changed. Try again.";
}

// ---------------------------------------------------------------------------
// Request functions
// ---------------------------------------------------------------------------

export async function fetchAccounts(): Promise<readonly AccountView[]> {
  const body = await request<
    { items?: readonly AccountView[] } | readonly AccountView[]
  >(accountPaths.list);
  if (Array.isArray(body)) return body;
  return (body as { items?: readonly AccountView[] }).items ?? [];
}

export async function fetchAccountHistory(): Promise<
  readonly AccountEventView[]
> {
  const body = await request<{ items?: readonly AccountEventView[] }>(
    accountPaths.history,
  );
  return body.items ?? [];
}

/**
 * Creates the account and passes its one-time password — with what became
 * of the email that carried it — to `onTemporaryPassword`. Resolves with the
 * account only — the password is not part of the result. `temporaryPassword`
 * is the one the administrator typed; `undefined` sends no such key and the
 * server generates one (an empty string would be refused as too short).
 */
export async function createAccount(
  input: CreateAccountVariables,
  temporaryPassword: string | undefined,
  onTemporaryPassword: (
    password: string,
    invitation: InvitationOutcome,
    user: AccountView,
  ) => void,
): Promise<AccountView> {
  const body: CreateAccountInput = {
    ...input,
    ...(temporaryPassword !== undefined ? { temporaryPassword } : {}),
  };
  const result = await request<{
    user: AccountView;
    temporaryPassword: string;
    invitation: InvitationOutcome;
  }>(accountPaths.create, { method: "POST", body: JSON.stringify(body) });
  onTemporaryPassword(result.temporaryPassword, result.invitation, result.user);
  return result.user;
}

export function renameAccount(
  userId: string,
  input: RenameAccountInput,
): Promise<AccountView> {
  return request<AccountView>(accountPaths.name(userId), {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function assignRole(
  userId: string,
  input: AssignRoleInput,
): Promise<AccountView> {
  return request<AccountView>(accountPaths.role(userId), {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function deactivateAccount(
  userId: string,
  input: DeactivateAccountInput,
): Promise<AccountView> {
  return request<AccountView>(accountPaths.deactivate(userId), {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function setAdministration(
  userId: string,
  input: SetAdministrationInput,
): Promise<AccountView> {
  return request<AccountView>(accountPaths.administration(userId), {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/**
 * Resets the account's password and passes the new one-time password — with
 * what became of the email that carried it — to `onTemporaryPassword`.
 * Resolves with nothing. With `temporaryPassword` the request carries it as
 * its body; without, the request has no body at all, as it always had.
 */
export async function resetPassword(
  userId: string,
  temporaryPassword: string | undefined,
  onTemporaryPassword: (password: string, invitation: InvitationOutcome) => void,
): Promise<void> {
  const body: ResetPasswordInput | undefined =
    temporaryPassword !== undefined ? { temporaryPassword } : undefined;
  const result = await request<{
    temporaryPassword: string;
    invitation: InvitationOutcome;
  }>(accountPaths.resetPassword(userId), {
    method: "POST",
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  onTemporaryPassword(result.temporaryPassword, result.invitation);
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useAccounts(
  options: { enabled?: boolean } = {},
): UseQueryResult<readonly AccountView[]> {
  return useQuery({
    queryKey: accountsQueryKey,
    queryFn: fetchAccounts,
    enabled: options.enabled ?? true,
    staleTime: 10_000,
  });
}

/** The latest account changes, newest first; refreshed by every account mutation. */
export function useAccountHistory(
  options: { enabled?: boolean } = {},
): UseQueryResult<readonly AccountEventView[]> {
  return useQuery({
    queryKey: accountHistoryQueryKey,
    queryFn: fetchAccountHistory,
    enabled: options.enabled ?? true,
    staleTime: 10_000,
  });
}

export interface TemporaryPasswordOptions<Extra extends unknown[] = []> {
  /**
   * Receives the one-time password and the outcome of the email that
   * carried it. Called once, before the mutation resolves; the mutation's
   * own result never carries the password.
   */
  onTemporaryPassword: (
    password: string,
    invitation: InvitationOutcome,
    ...extra: Extra
  ) => void;
  /**
   * Reads the temporary password the administrator typed, if any, at the
   * moment the request is sent — `undefined` (or absent) has the server
   * generate one. An accessor rather than a mutation variable so the value
   * never enters the mutation cache and lives only in the dialog's state.
   */
  chosenTemporaryPassword?: () => string | undefined;
}

/** What the create mutation is called with: the fields, never the password. */
export type CreateAccountVariables = Omit<CreateAccountInput, "temporaryPassword">;

export function useCreateAccount(
  options: TemporaryPasswordOptions<[user: AccountView]>,
): UseMutationResult<AccountView, unknown, CreateAccountVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAccountVariables) =>
      createAccount(
        input,
        options.chosenTemporaryPassword?.(),
        options.onTemporaryPassword,
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: accountsQueryKey }),
  });
}

export type RenameAccountVariables = Readonly<
  { userId: string } & RenameAccountInput
>;

/**
 * Renames the account under its version. The answer is the row as the
 * server now holds it — with the bumped version a following role change in
 * the same Save must carry — so the caller reads it from `mutateAsync`
 * rather than waiting for the list to refetch.
 */
export function useRenameAccount(): UseMutationResult<
  AccountView,
  unknown,
  RenameAccountVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...input }: RenameAccountVariables) =>
      renameAccount(userId, input),
    // Success or conflict alike, the list is what the server now holds.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: accountsQueryKey }),
  });
}

export type AssignRoleVariables = Readonly<
  { userId: string } & AssignRoleInput
>;

export function useAssignRole(): UseMutationResult<
  AccountView,
  unknown,
  AssignRoleVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...input }: AssignRoleVariables) =>
      assignRole(userId, input),
    // Success or conflict alike, the list is what the server now holds.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: accountsQueryKey }),
  });
}

export type DeactivateAccountVariables = Readonly<
  { userId: string } & DeactivateAccountInput
>;

export function useDeactivateAccount(): UseMutationResult<
  AccountView,
  unknown,
  DeactivateAccountVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...input }: DeactivateAccountVariables) =>
      deactivateAccount(userId, input),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: accountsQueryKey }),
  });
}

export type SetAdministrationVariables = Readonly<
  { userId: string } & SetAdministrationInput
>;

/** Grants or revokes administration; success or refusal alike, the list is reloaded. */
export function useSetAdministration(): UseMutationResult<
  AccountView,
  unknown,
  SetAdministrationVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...input }: SetAdministrationVariables) =>
      setAdministration(userId, input),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: accountsQueryKey }),
  });
}

export function useResetPassword(
  options: TemporaryPasswordOptions,
): UseMutationResult<void, unknown, { userId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }: { userId: string }) =>
      resetPassword(
        userId,
        options.chosenTemporaryPassword?.(),
        options.onTemporaryPassword,
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: accountsQueryKey }),
  });
}

// ===========================================================================
// Assignees (Task 11)
//
// The people an order can be assigned to — active service advisors and
// technicians — from the Parts-only `GET /api/assignees`. The order form and
// the edit sheet read it; the administrator-only account list above is not
// involved, so a Parts session never sees email addresses or account flags.
// ===========================================================================

export type { AssigneeDirectory, AssigneeOption } from "../../shared/contracts.js";

export const assigneesQueryKey = ["assignees"] as const;

export const assigneePaths = { list: "/api/assignees" } as const;

export function fetchAssignees(): Promise<AssigneeDirectory> {
  return request<AssigneeDirectory>(assigneePaths.list);
}

/**
 * Loads the assignable people. Fresh for a minute: the list changes only
 * when an administrator creates or deactivates an account, and a stale name
 * costs nothing — the server validates the chosen id on every command.
 */
export function useAssignees(
  options: { enabled?: boolean } = {},
): UseQueryResult<AssigneeDirectory> {
  return useQuery({
    queryKey: assigneesQueryKey,
    queryFn: fetchAssignees,
    enabled: options.enabled ?? true,
    staleTime: 60_000,
  });
}
